import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage, Language } from './contexts/LanguageContext';
import { NoticeBar } from './components/NoticeBar';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FloatingContact } from './components/ContactButtons';
import { Home } from './pages/Home';
import { Contact } from './pages/Contact';
import { PricingPage } from './pages/PricingPage';
import { HowItWorksPage } from './pages/HowItWorksPage';
import { FAQPage } from './pages/FAQPage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { RefundPolicyPage } from './pages/RefundPolicyPage';
import { GraciasPage } from './pages/GraciasPage';
import { GuiaPage } from './pages/GuiaPage';
import { PedidoPage } from './pages/PedidoPage';
import { FotoPage } from './pages/FotoPage';
import { RevisionPage } from './pages/RevisionPage';
import { AppointmentPage } from './pages/AppointmentPage';

// Whenever the route (page) changes, jump back to the top. Without this, a SPA
// keeps the previous scroll position, so a new page opens scrolled down instead
// of at its top. We reset both window and the scrolling element to be safe
// across browsers.
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
  }, [pathname]);
  return null;
}

// Cada página pública tiene su propio título ("Precios | Noor Passport Services").
// Así Google puede mostrar cada página con un nombre claro debajo del resultado de
// la marca. La portada y las páginas internas muestran solo "Noor Passport Services".
const BRAND = 'Noor Passport Services';
const PAGE_TITLES: Record<string, Record<Language, string>> = {
  '/how-it-works': {
    en: 'How it works',
    es: 'Cómo funciona',
    ht: 'Kijan li mache',
    vi: 'Cách hoạt động'
  },
  '/pricing': {
    en: 'Packages & pricing',
    es: 'Paquetes y precios',
    ht: 'Pakè ak pri',
    vi: 'Gói và bảng giá'
  },
  '/faq': {
    en: 'Frequently asked questions',
    es: 'Preguntas frecuentes',
    ht: 'Kesyon moun poze souvan',
    vi: 'Câu hỏi thường gặp'
  },
  '/contact': {
    en: 'Contact',
    es: 'Contacto',
    ht: 'Kontak',
    vi: 'Liên hệ'
  },
  '/guia': {
    en: 'Which form do I need?',
    es: '¿Qué formulario necesito?',
    ht: 'Ki fòm mwen bezwen?',
    vi: 'Tôi cần mẫu đơn nào?'
  },
  '/privacy-policy': {
    en: 'Privacy policy',
    es: 'Política de privacidad',
    ht: 'Politik vi prive',
    vi: 'Chính sách quyền riêng tư'
  },
  '/terms-of-service': {
    en: 'Terms of service',
    es: 'Términos de servicio',
    ht: 'Kondisyon sevís',
    vi: 'Điều khoản dịch vụ'
  },
  '/refund-policy': {
    en: 'Refund policy',
    es: 'Política de reembolsos',
    ht: 'Politik ranbousman',
    vi: 'Chính sách hoàn tiền'
  }
};
function PageTitle() {
  const {
    pathname
  } = useLocation();
  const {
    language
  } = useLanguage();
  useEffect(() => {
    const key = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
    const p = PAGE_TITLES[key];
    document.title = p ? `${p[language]} | ${BRAND}` : BRAND;
  }, [pathname, language]);
  return null;
}

// "Skip to main content" link — the first thing a keyboard / screen-reader user
// reaches. It stays visually hidden until focused, then jumps past the nav
// straight to the page content.
const SKIP_LABEL: Record<Language, string> = {
  en: 'Skip to main content',
  es: 'Saltar al contenido principal',
  ht: 'Ale dirèkteman nan kontni prensipal la',
  vi: 'Chuyển đến nội dung chính'
};
function SkipLink() {
  const {
    language
  } = useLanguage();
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-full focus:bg-brand-textPrimary focus:text-white focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-0" data-mp="1m4wauaqsl1d2">
      {SKIP_LABEL[language]}
    </a>;
}
export function App() {
  return <LanguageProvider data-id="element-1" data-mp="m6fayl65ulfs">
      <BrowserRouter data-id="element-2" data-mp="m5x6505fmyw9">
        <ScrollToTop data-id="element-3" data-mp="m5nel84qojog" />
        <PageTitle data-id="element-4" data-mp="m549is06tpru" />
        <div className="min-h-screen flex flex-col bg-brand-bg font-sans text-brand-textPrimary" data-id="element-5" data-mp="m4m1yz82rp4m">
          <SkipLink data-id="element-6" data-mp="h4znynkfa4ur" />
          {/* Legal disclosure: shown at the very top of the page but allowed to
              scroll away normally — it is NOT pinned, so it doesn't eat the
              screen (especially on mobile). */}
          <NoticeBar data-id="element-7" data-mp="14wez95u6fwi5" />
          {/* Only the compact header stays pinned at the top while scrolling. */}
          <div className="sticky top-0 z-50" data-id="element-8" data-mp="zx29ac80e9bc">
            <Header data-id="element-9" data-mp="zzww2aehw67x" />
          </div>
          <main id="main-content" tabIndex={-1} className="flex-grow focus:outline-none" data-id="element-10" data-mp="vwrtk7uf04ja">
            <Routes data-id="element-11" data-mp="3koufpdpijwav">
              <Route path="/" element={<Home data-id="element-13" data-mp="3gmh8ekv4bqbz" />} data-id="element-12" data-mp="3klyd3c2q0jg3" />
              <Route path="/how-it-works" element={<HowItWorksPage data-id="element-15" data-mp="3gl63ghpxscja" />} data-id="element-14" data-mp="3gm7qlkk88phd" />
              <Route path="/pricing" element={<PricingPage data-id="element-17" data-mp="1w5gecryhzy4s" />} data-id="element-16" data-mp="1w4ezeced2rhs" />
              <Route path="/faq" element={<FAQPage data-id="element-19" data-mp="2060bhvj2n1qx" />} data-id="element-18" data-mp="1w69nfzkxizq3" />
              <Route path="/contact" element={<Contact data-id="element-21" data-mp="206js98wzjk3d" />} data-id="element-20" data-mp="2058r7guxcqfy" />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage data-id="element-23" data-mp="254v2hkr6zwm4" />} data-id="element-22" data-mp="203ntxjh86tee" />
              <Route path="/terms-of-service" element={<TermsOfServicePage data-id="element-25" data-mp="29wv7vsq17hq3" />} data-id="element-24" data-mp="253scrg5lw07s" />
              <Route path="/refund-policy" element={<RefundPolicyPage data-id="element-27" data-mp="2evptf2fdvkdw" />} data-id="element-26" data-mp="2euwswj70pr47" />
              <Route path="/gracias" element={<GraciasPage data-id="element-29" data-mp="2jtr8zddlp1km" />} data-id="element-28" data-mp="2eumxuh748trn" />
              <Route path="/guia" element={<GuiaPage data-id="element-31" data-mp="2js6f7pz41mnt" />} data-id="element-30" data-mp="2jsycd58cvsg7" />
              <Route path="/pedido" element={<PedidoPage data-id="element-33" data-mp="2nvcuto844q6y" />} data-id="element-32" data-mp="2nvl5rakodlfi" />
              <Route path="/foto" element={<FotoPage data-id="element-35" data-mp="2nts0nzppn99m" />} data-id="element-34" data-mp="2nw4h28ftlbkk" />
              <Route path="/revision" element={<RevisionPage data-id="element-37" data-mp="2su5vuh67qoq8" />} data-id="element-36" data-mp="2sv7hffkuutm7" />
              <Route path="/appointment" element={<AppointmentPage data-id="element-39" data-mp="tid8lc988xqp" />} data-id="element-38" data-mp="2ssblfwskif0e" />
            </Routes>
          </main>
          <Footer data-id="element-40" data-mp="ygojzrf67gb1" />
          {/* Always-visible Call + WhatsApp buttons */}
          <FloatingContact data-id="element-41" data-mp="yir6w2l10tbj" />
        </div>
      </BrowserRouter>
    </LanguageProvider>;
}